/**
 * Converts pasted rich text (Google Docs, Word, web pages) into the small
 * Markdown subset our RichText renderer supports. Everything else — fonts,
 * colours, spans, tables of styling junk — is dropped on purpose so pasted
 * copy stays on brand.
 */

function isBold(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag === "strong" || tag === "b") return true;
  const weight = (el as HTMLElement).style?.fontWeight ?? "";
  if (!weight) return false;
  if (weight === "bold" || weight === "bolder") return true;
  const numeric = Number(weight);
  return Number.isFinite(numeric) && numeric >= 600;
}

function isItalic(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag === "em" || tag === "i") return true;
  return ((el as HTMLElement).style?.fontStyle ?? "") === "italic";
}

function collapse(text: string): string {
  return text.replace(/[\u00a0\s]+/g, " ");
}

function inline(node: Node): string {
  if (node.nodeType === 3) return collapse(node.nodeValue ?? "");
  if (node.nodeType !== 1) return "";
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (tag === "br") return "\n";
  if (tag === "script" || tag === "style") return "";

  let inner = Array.from(el.childNodes).map(inline).join("");
  if (!inner.trim()) return inner;

  if (tag === "a") {
    const href = el.getAttribute("href") ?? "";
    const clean = googleRedirect(href);
    if (clean) return `[${inner.trim()}](${clean})`;
    return inner;
  }

  // Wrap the trimmed core so markers never sit against whitespace.
  const lead = inner.match(/^\s*/)?.[0] ?? "";
  const tail = inner.match(/\s*$/)?.[0] ?? "";
  const core = inner.trim();
  if (isBold(el)) inner = `${lead}**${core}**${tail}`;
  else if (isItalic(el)) inner = `${lead}*${core}*${tail}`;
  return inner;
}

/** Google Docs wraps external links in a redirect; keep the real target. */
function googleRedirect(href: string): string {
  if (!href) return "";
  const match = href.match(/^https?:\/\/(?:www\.)?google\.com\/url\?(?:[^#]*&)?q=([^&#]+)/);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  return href;
}

const BLOCK_TAGS = [
  "p",
  "div",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "table",
  "tbody",
  "thead",
  "tr",
  "td",
  "th",
  "section",
  "article",
  "hr",
];

function hasBlockChild(el: Element): boolean {
  return Array.from(el.children).some((c) => BLOCK_TAGS.includes(c.tagName.toLowerCase()));
}

function block(node: Node, out: string[]): void {
  if (node.nodeType === 3) {
    const text = collapse(node.nodeValue ?? "");
    if (text.trim()) out.push(text.trim());
    return;
  }
  if (node.nodeType !== 1) return;
  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case "script":
    case "style":
    case "meta":
    case "head":
      return;
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6": {
      const level = Math.min(3, Number(tag[1]));
      const text = inline(el).trim();
      if (text) out.push(`${"#".repeat(level)} ${text}`);
      return;
    }
    case "ul":
    case "ol": {
      const items = Array.from(el.children).filter((c) => c.tagName.toLowerCase() === "li");
      const lines: string[] = [];
      items.forEach((li, i) => {
        // Flatten nested lists into the same level.
        const nested = Array.from(li.children).filter((c) =>
          ["ul", "ol"].includes(c.tagName.toLowerCase()),
        );
        nested.forEach((n) => n.remove());
        const text = inline(li).trim();
        if (text) lines.push(tag === "ol" ? `${i + 1}. ${text}` : `- ${text}`);
        nested.forEach((n) => block(n, lines));
      });
      if (lines.length) out.push(lines.join("\n"));
      return;
    }
    case "li": {
      const text = inline(el).trim();
      if (text) out.push(`- ${text}`);
      return;
    }
    case "blockquote": {
      const text = inline(el).trim();
      if (text) out.push(`> ${text}`);
      return;
    }
    case "hr":
      out.push("---");
      return;
    case "p":
    case "div":
    case "section":
    case "article":
    case "body":
    case "html":
    case "table":
    case "tbody":
    case "tr":
    case "td":
    default: {
      if (hasBlockChild(el)) {
        Array.from(el.childNodes).forEach((child) => block(child, out));
        return;
      }
      const text = inline(el).trim();
      if (text) out.push(text);
      return;
    }
  }
}

export function htmlToMarkdown(html: string): string {
  if (!html?.trim()) return "";
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return "";
  }
  const out: string[] = [];
  Array.from(doc.body?.childNodes ?? []).forEach((node) => block(node, out));
  return out
    .join("\n\n")
    .replace(/\*\*\s*\*\*/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export default htmlToMarkdown;
